/**
 * Host-side orchestrator for an embedded insight.
 *
 * Owns the Web Worker, the HTTP transport, and the click/change
 * subscription sets. Delegates the state-machine concerns to
 * `HostStateMachine` so this file stays focused on wiring:
 *
 *   Worker → Host                   Runner action
 *   ─────────────────────────────────────────────────────────────
 *   ready                           mark worker ready, kick off first run
 *   query / query_batch             POST /api/query/batch, route responses
 *   state_set                       hand off to HostStateMachine (coalesces)
 *   subscribe_click / _change       remember target id
 *   render / render_patch           validate + parse to tree (gated by passId)
 *   pass_committed                  HostStateMachine.acceptCommit → onStateChange
 *   diagnostic                      forward to the host's diagnostic pipeline
 *   error                           HostStateMachine.noteError + onError
 *
 * Compared to the previous implementation, the state-management
 * concerns (namespaced state, passId/nonce, coalesce window,
 * commit-time `onStateChange`, stale-pass dropping) live in
 * `HostStateMachine`. The runner just wires its callbacks to
 * worker postMessage and transport.cancelInFlight.
 */

import type {
  ChangeValue,
  HostMessage,
  NamespacedState,
  RenderOp,
  WorkerLocalMessage,
} from "./protocol.ts";

import { bundleInsight } from "./bundle-insight.ts";
import {
  InvalidRenderOps,
  validateAndParse,
  type TreeNode,
} from "./validate-render-op.ts";
import { HttpClient, type QueryMeta } from "./http-client.ts";
import { newExId } from "./exId.ts";
import { HostStateMachine } from "./host-state-machine.ts";

/** Inputs needed to start a runner against an insight. */
export interface InsightRunnerOptions {
  /** Raw JS source — same body the API returned from `/source`. */
  source: string;
  /** Base URL of insights-api (for `POST /api/query`). */
  apiUrl: string;
  /** Insight id this runner is bound to. */
  insightId: string;
  /** Bearer token scoped to `insightId`. */
  token: string;
  /**
   * Optional initial host-set viewer state. The worker kicks off
   * with this map merged into the namespaced state envelope so
   * first-paint reflects remembered filter selections. Defaults to
   * `{}`.
   */
  initialState?: Record<string, unknown>;
  /**
   * Optional initial author-set state (the `sbi.state(...)` side
   * of the namespace). Only honoured when the embed page is
   * same-origin with `apiUrl` — third-party embed pages have this
   * silently dropped so they can't forge open drilldowns on first
   * paint. The dashboard's PDF export flow uses this to round-trip
   * the live drilldown selection through the print template.
   */
  initialAuthorState?: Record<string, unknown>;
  /**
   * Embed-token-pinned filter values, sourced from the source-fetch
   * response's `locked_filters` payload. Server-authoritative; the
   * caller cannot derive or override this — it reflects what the
   * token was minted with. Seeds the `locked` namespace before the
   * first pass; subsequent host / author writes never reach these
   * keys because the worker merges `{...host, ...author, ...locked}`.
   */
  initialLockedState?: Record<string, unknown>;
  /**
   * Called once per render pass, on the structural shell. The
   * second argument is the per-pass field → format string map.
   * Pass-scoped dedupe state should be reset here.
   */
  onRender: (tree: TreeNode[], formats: Record<string, string>) => void;
  /**
   * Called for every incremental update after `onRender` — one
   * call per resolved marker. NOT a pass boundary.
   */
  onPatch?: (tree: TreeNode[], formats: Record<string, string>) => void;
  /** Called for fatal errors that take the runner out. */
  onError: (err: Error) => void;
  /**
   * Fires once, immediately after the first `ready_ack` has been
   * posted to the worker.
   */
  onWorkerStarted?: () => void;
  /**
   * Fires when a new pass starts — host or worker mutated state and
   * the commit window has flushed. Use this to dim/pulse dynamic
   * leaves while queries resolve.
   */
  onRerunStart?: () => void;
  /**
   * Fires when the current pass terminates — either it committed
   * (matched nonce on `pass_committed`) or it errored. Pairs with
   * `onRerunStart` so consumers can flip `aria-busy` symmetrically.
   * Cancelled passes do NOT fire `onRerunEnd` on the cancel; the
   * superseding pass fires its own.
   */
  onRerunEnd?: (status: "committed" | "errored") => void;
  /** Fires when the worker dispatches a `sbi.data.query`. */
  onQuery?: (q: { id: string; sql: string; params: ReadonlyArray<unknown> }) => void;
  /**
   * Fires when the API responds to a previously dispatched query.
   * Forwarded regardless of pass — the host's pending-query counter
   * was incremented on dispatch and must decrement on response, or
   * `lucent-print-ready` stops firing.
   */
  onQueryResolved?: (
    r:
      | {
          id: string;
          status: "ok";
          rows: number;
          sql?: string;
          /**
           * True when photon served this from cache. Host-only diagnostic —
           * it arrives beside the worker message, never on it, so insight
           * source cannot see it. Absent against a server too old to send it.
           */
          cached?: boolean;
        }
      | { id: string; status: "error"; message: string; code: string; sql?: string },
  ) => void;
  /**
   * Fires once per **committed** pass — coalesced or cancelled
   * mutations do NOT emit. The payload is the namespaced state
   * `{ host, author }` actually rendered by the embed, so the
   * dashboard's `liveState` mirror always matches what's on
   * screen (load-bearing for the PDF-export "freeze current
   * state" flow).
   */
  onStateChange?: (state: Readonly<NamespacedState>) => void;
  /**
   * Fires when the worker emits a non-fatal diagnostic (e.g.
   * `host_author_state_collision`). Same pipeline as per-chart
   * diagnostics so the embed host can dedupe + coalesce.
   */
  onDiagnostic?: (code: string, detail: Record<string, unknown>) => void;
  /** Optional `fetch` override forwarded to `HttpClient` (test seam). */
  fetchImpl?: typeof fetch;
}

/**
 * Lifecycle: `new InsightRunner(...)` → `await start()` → `destroy()`.
 *
 * State mutation API:
 *   - `setState(partial)` — SHALLOW merges into the `host` half of
 *     the namespaced state. `undefined` deletes the key; `null`
 *     writes null.
 *   - `replaceState(state)` — replaces the `host` half wholesale.
 *
 * Both schedule a coalesced rerun. Multiple calls within one
 * animation frame land as a single pass.
 */
export class InsightRunner {
  private worker: Worker | null = null;
  private transport: HttpClient | null = null;

  private workerReady = false;
  private firstRunSent = false;
  private clickSubs = new Set<string>();
  private changeSubs = new Set<string>();
  /**
   * Latest flat op array we received from the worker — kept so a
   * `render_patch` can mutate just the affected indices and we can
   * re-validate the tree without asking the worker for a fresh
   * snapshot. Reset on every `render` message, mutated in place on
   * every `render_patch`.
   */
  private latestOps: RenderOp[] = [];

  private readonly state: HostStateMachine;

  /**
   * Snapshot of `sbi.state(k, …)` declarations from the most recently
   * accepted `pass_committed`. The embed exposes this to the embedding
   * page so save-as-view can filter the persisted author state against
   * the live insight's declared surface (drop keys whose `sbi.state`
   * call was removed in a later revision). Empty until first commit.
   * Stale-pass `pass_committed` messages don't update this — the
   * runner only stores the keys when `acceptCommit` accepts the pass.
   */
  private latestDeclaredStateKeys: ReadonlyArray<string> = [];

  /**
   * Keys seeded into the `author` half of the state map at runner
   * construction (via `opts.initialAuthorState`). On the first
   * accepted commit, the runner diffs this against the worker's
   * declared-state set and emits a `state_key_ignored` diagnostic
   * for every seeded key the current source no longer declares
   * (e.g. a saved view carrying a state key the author has since
   * removed). Once the first-commit diff has run, the set is
   * cleared so subsequent passes don't re-emit.
   */
  private seededAuthorStateKeys: Set<string>;
  private firstCommitProcessed = false;

  constructor(private readonly opts: InsightRunnerOptions) {
    this.state = new HostStateMachine({
      onCommit: ({ passId, nonce, state }) => {
        // A coalesced commit window has flushed. Cancel any HTTP
        // queries from the prior pass before the worker re-executes
        // — their results would be discarded by the worker anyway.
        this.transport?.cancelInFlight();
        this.worker?.postMessage({
          type: "rerun",
          reason: "state_change",
          passId,
          nonce,
          state,
        } satisfies HostMessage);
      },
      onPassStart: () => this.opts.onRerunStart?.(),
      onPassEnd: (status) => this.opts.onRerunEnd?.(status),
      onCommitted: (state) => this.opts.onStateChange?.(state),
    });
    this.state.seed({
      host: opts.initialState ?? {},
      author: opts.initialAuthorState ?? {},
      locked: opts.initialLockedState ?? {},
    });
    this.seededAuthorStateKeys = new Set(
      opts.initialAuthorState ? Object.keys(opts.initialAuthorState) : [],
    );
  }

  /**
   * Boot the worker and HTTP transport.
   */
  async start(): Promise<void> {
    const workerUrl = bundleInsight(this.opts.source);
    this.worker = new Worker(workerUrl);
    this.worker.addEventListener(
      "message",
      (e: MessageEvent<WorkerLocalMessage>) => this.handleWorkerMessage(e.data),
    );
    this.worker.addEventListener("error", (e) => {
      const detail =
        e.message ||
        (e.error instanceof Error ? e.error.message : "") ||
        (e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : "") ||
        "no detail (likely a syntax error in the bundled worker script)";
      // Pre-bootstrap worker failure (syntax error in the bundled
      // script, etc.). No `exId` came back from the worker — its
      // error handlers haven't installed yet — so we mint one here.
      const exId = newExId();
      console.error(`[EX:${exId}] worker error event`, e.error ?? e);
      const err = new Error(`worker error: ${detail}`) as Error & { exId?: string };
      err.exId = exId;
      this.opts.onError(err);
    });
    this.worker.addEventListener("messageerror", (e) => {
      const exId = newExId();
      console.error(`[EX:${exId}] worker messageerror`, e.data);
      const err = new Error(
        `worker messageerror: ${String(e.data ?? "no detail")}`,
      ) as Error & { exId?: string };
      err.exId = exId;
      this.opts.onError(err);
    });

    this.transport = new HttpClient({
      apiUrl: this.opts.apiUrl,
      token: this.opts.token,
      insightId: this.opts.insightId,
      onMessage: (m, meta) => this.handleHostMessage(m, meta),
      onError: (err) => this.opts.onError(err),
      ...(this.opts.fetchImpl !== undefined ? { fetchImpl: this.opts.fetchImpl } : {}),
    });
    await this.transport.ready();
  }

  /** Forward a click event from a rendered op back into the worker. */
  notifyClick(
    targetId: string,
    datum?: Record<string, unknown>,
    rowIndex?: number,
  ): void {
    if (!this.clickSubs.has(targetId)) return;
    const msg: HostMessage = {
      type: "click",
      event: { targetId, datum, rowIndex },
    };
    this.worker?.postMessage(msg);
  }

  /** Forward a filter change event from the host UI to the worker. */
  notifyChange(targetId: string, value: ChangeValue): void {
    if (!this.changeSubs.has(targetId)) return;
    const msg: HostMessage = {
      type: "change",
      event: { targetId, value },
    };
    this.worker?.postMessage(msg);
  }

  /**
   * Shallow-merge a partial into the `host` half of the namespaced
   * viewer state and trigger a coalesced rerun.
   *
   * Semantics:
   *   - Keys in `partial` write through.
   *   - `undefined` values delete the key (so callers can clear a
   *     filter with `setState({region: undefined})`).
   *   - `null` writes null (kept distinct from delete on purpose).
   *
   * Multiple `setState` calls within one animation frame coalesce
   * into a single `rerun`. The `state-change` DOM event fires on
   * commit, not on intent, so coalesced + cancelled mutations
   * never reach the embedder.
   */
  setState(partial: Record<string, unknown>): void {
    this.state.setHost(partial);
  }

  /**
   * Replace the `host` half of the namespaced viewer state
   * wholesale. Useful when the embedder needs "exactly this set
   * of filters, no others" semantics — e.g. a global Reset.
   */
  replaceState(state: Record<string, unknown>): void {
    this.state.replaceHost(state);
  }

  /**
   * Snapshot of the keys author code declared via `sbi.state(k, …)`
   * during the most recently committed pass. Empty array until the
   * first commit. The embedding page uses this to filter saved-view
   * payloads against the live insight's declared-state surface; stale
   * keys whose declarations were removed get dropped before persist.
   */
  getDeclaredStateKeys(): ReadonlyArray<string> {
    return this.latestDeclaredStateKeys;
  }

  /** Tear down the worker + transport. Idempotent. */
  destroy(): void {
    this.worker?.terminate();
    this.worker = null;
    this.transport?.close();
    this.transport = null;
  }

  // -------------------------------------------------------------------
  // Wiring
  // -------------------------------------------------------------------

  /** Worker → host message router. */
  private handleWorkerMessage(msg: WorkerLocalMessage): void {
    switch (msg.type) {
      case "ready":
        this.workerReady = true;
        this.maybeKickoff();
        return;

      case "query":
        this.opts.onQuery?.({ id: msg.id, sql: msg.sql, params: msg.params });
        void this.transport?.send(msg);
        return;

      case "query_batch":
        for (const q of msg.queries) {
          this.opts.onQuery?.({ id: q.id, sql: q.sql, params: q.params });
          void this.transport?.send({
            type: "query",
            id: q.id,
            sql: q.sql,
            params: q.params,
          });
        }
        return;

      case "state_set":
        // Worker setter — write into the `author` half of the
        // namespaced state and schedule a coalesced commit. Same
        // window as host-side `setState`, so a burst of setters +
        // host mutations within one animation frame collapse to a
        // single rerun.
        this.state.applyAuthorSet(msg.key, msg.value);
        return;

      case "subscribe_click":
        this.clickSubs.add(msg.targetId);
        return;

      case "subscribe_change":
        this.changeSubs.add(msg.targetId);
        return;

      case "render":
        // Drop stale-pass renders. The worker's `emitFromPass` helper
        // also drops these on the worker side, but we re-check here
        // because the host's view of `currentPassId` is authoritative
        // and may have advanced past the message in flight.
        if (!this.state.isCurrentPass(msg.passId)) return;
        try {
          this.latestOps = msg.ops.map((op) => ({ ...op }));
          const { tree, formats } = validateAndParse(msg.ops);
          this.opts.onRender(tree, formats);
        } catch (err) {
          const exId = newExId();
          console.error(`[EX:${exId}] render validation failed`, err);
          const out =
            err instanceof InvalidRenderOps
              ? err
              : err instanceof Error
                ? err
                : new Error(String(err));
          (out as Error & { exId?: string }).exId = exId;
          this.opts.onError(out);
        }
        return;

      case "render_patch":
        if (!this.state.isCurrentPass(msg.passId)) return;
        try {
          for (const update of msg.updates) {
            if (update.index < 0 || update.index >= this.latestOps.length) continue;
            this.latestOps[update.index] = update.op;
          }
          const { tree, formats } = validateAndParse(this.latestOps);
          if (this.opts.onPatch) this.opts.onPatch(tree, formats);
          else this.opts.onRender(tree, formats);
        } catch (err) {
          const exId = newExId();
          console.error(`[EX:${exId}] render_patch validation failed`, err);
          const out =
            err instanceof InvalidRenderOps
              ? err
              : err instanceof Error
                ? err
                : new Error(String(err));
          (out as Error & { exId?: string }).exId = exId;
          this.opts.onError(out);
        }
        return;

      case "pass_committed":
        // Both passId AND nonce must match the latest pass. A forged
        // `pass_committed` from author code (validator-banned-but-defence-
        // in-depth) carries a guessable passId but an unguessable
        // crypto.randomUUID() nonce. `acceptCommit` returns non-null
        // only when both match the active pass, so the declared-state
        // snapshot only updates on legitimate commits.
        if (this.state.acceptCommit(msg) !== null) {
          this.latestDeclaredStateKeys = msg.declaredStateKeys;
          // First-commit diff: any author-state keys seeded at runner
          // construction that the current source doesn't declare get a
          // `state_key_ignored` diagnostic. Once per runner lifetime —
          // the seeded set is cleared after the first commit so
          // subsequent renders don't re-emit.
          if (!this.firstCommitProcessed) {
            this.firstCommitProcessed = true;
            const declared = new Set(msg.declaredStateKeys);
            for (const key of this.seededAuthorStateKeys) {
              if (!declared.has(key)) {
                this.opts.onDiagnostic?.("state_key_ignored", { key });
              }
            }
            this.seededAuthorStateKeys.clear();
          }
        }
        return;

      case "diagnostic":
        this.opts.onDiagnostic?.(msg.code, msg.key ? { key: msg.key } : {});
        return;

      case "error": {
        // Author-code error during a pass — record the pass as errored
        // (drives `onRerunEnd` with status "errored") then surface to
        // the embedder. State is NOT rolled back per plan §"Error
        // semantics".
        //
        // The worker minted an `exId` and already logged it to the
        // browser console with the underlying error. We attach the
        // same tag to the Error we surface upstream so the embed UI
        // can render `[EX:<id>]` next to the user-facing banner.
        this.state.noteError();
        const err = new Error(msg.message) as Error & { exId?: string };
        if (msg.exId !== undefined) err.exId = msg.exId;
        this.opts.onError(err);
        return;
      }
    }
  }

  /**
   * HTTP transport → host: query_result, query_error.
   *
   * `meta` is the transport's host-only diagnostics channel. It is spread into
   * the `onQueryResolved` payload (host chrome) and deliberately *not* into
   * `msg`, which goes to the worker a few lines below — that separation is
   * what keeps `cached` out of reach of insight source.
   */
  private handleHostMessage(msg: HostMessage, meta?: QueryMeta): void {
    switch (msg.type) {
      case "query_result":
        this.opts.onQueryResolved?.({
          id: msg.id,
          status: "ok",
          rows: msg.rows.length,
          ...(msg.sql !== undefined ? { sql: msg.sql } : {}),
          ...(meta?.cached !== undefined ? { cached: meta.cached } : {}),
        });
        // Always forward to the worker — the worker side filters by
        // pass so a result for a superseded pass finds no pending
        // entry and silently drops. The host counter side-effects
        // (`printPendingQueries`) MUST run regardless of pass to
        // avoid leaking the counter.
        this.worker?.postMessage(msg);
        return;

      case "query_error":
        this.opts.onQueryResolved?.({
          id: msg.id,
          status: "error",
          message: msg.message,
          code: msg.code,
          ...(msg.sql !== undefined ? { sql: msg.sql } : {}),
        });
        this.worker?.postMessage(msg);
        return;

      // ready_ack / rerun / click / change come from this module's
      // internal bookkeeping, not the transport. Enumerated only
      // because TS demands an exhaustive switch on HostMessage.
      case "ready_ack":
      case "rerun":
      case "click":
      case "change":
        return;
    }
  }

  /**
   * Kick off the first run once the worker is ready. The
   * HostStateMachine mints a fresh passId + nonce for the first
   * pass via `prepareFirstAck`, so the worker can correlate its
   * first `pass_committed` with our commit accounting.
   */
  private maybeKickoff(): void {
    if (this.firstRunSent) return;
    if (!this.workerReady) return;
    const first = this.state.prepareFirstAck();
    const ack: HostMessage = {
      type: "ready_ack",
      passId: first.passId,
      nonce: first.nonce,
      state: first.state,
    };
    this.worker?.postMessage(ack);
    this.firstRunSent = true;
    this.opts.onWorkerStarted?.();
  }
}

export type { RenderTree, TreeNode } from "./validate-render-op.ts";
